// The demo half of the Writing list, derived from the filesystem.
//
// Each demo folder carries its own `meta.tsx`; this globs them. Previously the
// same metadata lived in a hand-maintained `DATA.demos` array, which meant a demo
// was three things in three places — a component, a route, and an array entry —
// and deleting the first two left it listed on the home page anyway. That is the
// failure this replaces: the list now says what the folder tree says.
//
// Essays already worked this way (`import.meta.glob('/content/writing/*.mdx')`),
// so both halves of the list are now derived rather than declared. Which is the
// same argument the demos themselves make: generate the artifact from its inputs
// instead of hand-posting it.
//
// Only `meta.tsx` is globbed, never the demo components — the metadata files are
// tiny, so the index page does not pull every demo's React and engine code into
// its bundle just to render a list of titles.
type DemoLink = { type: string; href: string; icon?: React.ReactNode }

export type DemoMeta = {
	title: string
	blurb: string
	label: string
	eyebrow?: string
	href: string
	/** Hidden from the Writing list, but the route and its capsule still work. */
	listed: boolean
	dates: string
	active?: boolean
	featured?: boolean
	description?: string
	hardProblem: string
	approach: string
	technologies?: string[]
	links?: DemoLink[]
	video?: string
	image?: string
	imageDark?: string
}

const modules = import.meta.glob<{ demo: DemoMeta }>(
	'/app/features/portfolio/demos/*/meta.tsx',
	{ eager: true },
)

// Sorted by path so ordering is stable across dev and build rather than
// depending on whatever order the bundler happened to resolve modules in.
export const ALL_DEMOS: DemoMeta[] = Object.keys(modules)
	.sort()
	.map((path) => modules[path]!.demo)

/** What the Writing list and home page render. */
export const LISTED_DEMOS: DemoMeta[] = ALL_DEMOS.filter((d) => d.listed)

/** Lookup for DemoCapsule — resolves unlisted demos too, since their pages still exist. */
export function findDemo(href: string): DemoMeta | undefined {
	return ALL_DEMOS.find((d) => d.href === href)
}
